import type { ClassDefinition } from '../../schemas/classDefinition';
import { barbaroAdvanced2to5Moves } from '../moves/barbaro/advanced-2-5';
import { barbaroAdvanced6to10Moves } from '../moves/barbaro/advanced-6-10';
import {
  barbaroStartingAutoMoves,
  barbaroDefenseStyleMoves,
  barbaroInstinctMoves,
} from '../moves/barbaro/initial';

/**
 * Definizione della classe **Barbaro** (manuale V1, rr. 1931-2233).
 *
 * **Dodicesima e ULTIMA classe DWR** trascritta — chiude la **Fase C** della
 * ROADMAP (data-entry classi). Il Barbaro è la più peculiare del manuale.
 * Pattern stress-testati a completamento del modello dati:
 *
 * - **3 archetipi origine-tematici** (Straniero / Selvaggio / Nomade),
 *   `associatedRace: undefined`. Unica eccezione delle 12 classi DWR a NON
 *   avere il legame razza→archetipo. Le altre 11 classi hanno 5 archetipi
 *   razza-tematici (Umano/Nano/Elfo/Halfling/Mezz'orco). Schema
 *   `ArchetypeSchema.associatedRace` è opzionale proprio per questa eccezione.
 *
 * - **`suggestedTitles` popolato (12 titoli)** — **PRIMO USO** del campo fra
 *   le 12 classi DWR. Il manuale (r. 1944) dedica una sezione «Titoli» solo
 *   al Barbaro; le altre 11 classi hanno `suggestedTitles: []`. Lo schema
 *   `ClassDefinitionSchema.suggestedTitles` ha `default([])` proprio per
 *   questa specificità.
 *
 * - **`StartingMoveChoiceGroup.expandableInAdvanced: true` sugli Istinti** —
 *   **PRIMO USO STRUTTURALE** del flag fra le 12 classi DWR. Lo schema lo
 *   definisce con `default(false)` proprio per gli Istinti del Barbaro
 *   (commento esplicito in `src/schemas/classDefinition.ts`). Il giocatore
 *   sceglie 1 Istinto al L1; le opzioni rimanenti possono essere prese al
 *   level-up tramite la mossa avanzata *Istinti Primordiali* (L2-5,
 *   description-only). La logica builder che onora il flag arriverà in
 *   Fase D (level-up).
 *
 * - **`maxBonds: 4`** — valore già visto (Ladro, Guerriero, Monaco, Paladino,
 *   Ranger). Range FI-3 osservato: 3 (Mago/Paladino — strutturalmente con
 *   override Nano) - 4 (Barbaro/Ladro/Guerriero/Monaco/Paladino/Ranger) -
 *   5 (Sciamano) - 6 (Bardo).
 *
 * - **Dado di danno d10** — terzo d10 fra le 12 classi (Barbaro/Guerriero/
 *   Paladino).
 *
 * - **`baseHp: 8`**, **`baseLoad: 10`** — valori standard del Barbaro.
 *
 * - **4 categorie di Aspetto** (Occhi / Corpo / Segni particolari / Vesti) —
 *   pattern noto.
 *
 * - **Due `startingChoiceGroups`** nella stessa classe — pattern non visto in
 *   nessuna delle 11 classi precedenti (tutte le altre hanno 1 solo gruppo
 *   iniziale). Stili di Difesa (pickCount: 1, expandable: false) + Istinti
 *   (pickCount: 1, expandable: true).
 *
 * - **`spellcasting: undefined`** — non casteggiante. Conferma del modello:
 *   7° classe non casteggiante DWR (Barbaro/Bardo/Guerriero/Ladro/Monaco/
 *   Ranger/Sciamano).
 *
 * **FI aperti nella PR Barbaro**:
 *
 * - **FI-30 (P3) nuovo**: ***Essere il Totem*** (L6-10) pattern UNICO «mossa
 *   che concede mossa specifica di altra classe non-multiclasse»
 *   (description-only).
 * - **FI-31 (P3) nuovo**: pattern trasversale «mossa avanzata che concede
 *   sotto-scelta da un set della classe» — 8+ casi su 12 classi
 *   (description-only).
 *
 * **FI-8 stub description-only**: *Banda di Guerra* (L2-5, gregario L2) +
 * *Il Khan dei Khan* (L6-10, evolve a L3) — refactor unificato Ranghi 1-3
 * nella PR FI-8 post-12 classi.
 *
 * **Niente cambi schema** — tutti i pattern peculiari sono coperti dallo
 * schema esistente.
 */
export const barbaroClassDefinition: ClassDefinition = {
  id: 'barbaro',
  name: 'Barbaro',

  // Manuale V1, rr. 1934-1938 (3 paragrafi narrativi)
  intro:
    "Fissa bene questi codardi. Non capiscono chi sei. Ti tremano davanti. E fanno bene, luridi vermi! Tu non sei come loro. Che tu provenga dalle lande ghiacciate del nord, dalle giungle esotiche del sud o dalle tribù delle montagne antiche, il tuo mondo è fatto di cicatrici e ossa rotte, di notti stellate passate all'addiaccio, di pelle bruciata dal sole e dal vento, di occhi che sanno leggere le tracce nella polvere e il presagio nel volo degli uccelli. Non di banchetti e chiacchiere. Loro si nascondono dietro leggi fatte di carta, tu affermi la forza del tuo pugno. Quale legge è più vera?\n\nE i tuoi compagni? Quegli smidollati ti guardano dall'alto in basso? Lasciali fare, idioti. Che tu combatta con il freddo acciaio e l'astuzia selvaggia di un re guerriero, che ti abbandoni alla furia cieca del berserker, o che invochi la forza del tuo animale totemico, voce ancestrale e spirito guida dei tuoi avi, quando il sangue comincerà a bagnare la terra, saranno loro a chiedere pietà.\n\nTu non ti accontenti di una vita mediocre e di qualche sporca moneta: vuoi qualcosa di più grande. Gloria che echeggia tra le montagne, vendetta per il tuo clan, fama che terrorizza i nemici, ricchezze antiche e perdute, il favore degli dèi e del totem… Perché la tua anima è forgiata nella fornace delle terre selvagge, tempra d'acciaio che non si piega, spirito libero che non conosce catene. Perché nessuno, ripeto, nessuno è potente come te! Ora dimostralo al mondo.",

  // Manuale V1, r. 1942 (31 nomi suggeriti, mix di culture)
  suggestedNames: [
    'Trams',
    'Si-Yi',
    'Priscilla',
    'Sen',
    'Xia',
    'Anneira',
    'Haepha',
    'Lur',
    'Shar',
    'Korrin',
    'Nkosi',
    'Fafnir',
    'Qua',
    'Sacer',
    "Vercin'geto",
    'Barbozar',
    'Clovis',
    'Frael',
    'Thra-raxes',
    'Sillius',
    'Khamisi',
    'Sha-Sheena',
    'Nathreya',
    'Delwar',
    'Freafrea',
    'Garva',
    'Skull',
    'Convara',
    'Glor',
    'Cyn',
    'Confreya',
  ],

  // Manuale V1, r. 1946 — PRIMO USO `suggestedTitles` fra le 12 classi DWR.
  // Il Barbaro è l'unica classe con sezione «Titoli» nel manuale.
  suggestedTitles: [
    'il Glorioso',
    "l'Affamato",
    "l'Irascibile",
    "l'Invincibile",
    "l'Insaziabile",
    'Spacca Nemici',
    'Spezza Ossa',
    'il Gioioso',
    'il Malinconico',
    "l'Onnipotente",
    'il Gigante',
    'il Trionfante',
  ],

  // Manuale V1, rr. 1948-1954
  aspetti: [
    {
      label: 'Occhi',
      options: ['Occhi tormentati', 'Occhi spiritati', 'Occhi selvaggi'],
    },
    {
      label: 'Corpo',
      options: ['Muscoli possenti', 'Pelle e ossa', 'Corpo flessuoso'],
    },
    {
      label: 'Segni particolari',
      options: ['Strani tatuaggi', 'Gioielli esotici', 'Senza segni particolari'],
    },
    {
      label: 'Vesti',
      options: ['Stracci', 'Pellicce', 'Sete', 'Vestiti inadatti al clima'],
    },
  ],

  // Manuale V1, rr. 1956-1962
  damageDie: 'd10',
  baseHp: 8,
  baseLoad: 10,

  // Manuale V1, rr. 1964-1972
  alignmentPrecepts: {
    buono: 'Ti metti in pericolo per il bene di un amico o di una persona amata.',
    legale: 'Rispetti una tradizione del tuo popolo, pagandone le conseguenze.',
    neutrale: 'Insegni a qualcuno le tradizioni del tuo popolo.',
    caotico: 'Ignori una convenzione sociale del mondo civilizzato.',
    malvagio: 'Dai una dimostrazione di brutalità su qualcuno per terrorizzare gli altri.',
  },

  // Manuale V1, rr. 1985-1989. maxBonds: 4 (4 templates ↔ 4, FI-3 rispettato).
  bondTemplates: [
    '_____ è gracile e sciocco, ma mi fa ridere. Devo evitare che questo fragile giocattolo si rompa!',
    'Non capisco le vie di _____, e questo mi infastidisce.',
    '_____ vede cose che io non vedo. Devo osservarlo meglio!',
    '_____ condivide la mia fame di gloria. La terra tremerà al nostro passaggio!',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 1991-1994.
  // **Eccezione del Barbaro**: 3 archetipi ORIGINE-TEMATICI (Straniero /
  // Selvaggio / Nomade), `associatedRace: undefined`. Le altre 11 classi hanno
  // 5 archetipi razza-tematici.
  archetypes: [
    {
      id: 'straniero',
      name: 'Straniero',
      // associatedRace omesso (undefined) — eccezione del Barbaro
      description:
        "All'inizio di ogni sessione, il GM ti farà una domanda a proposito della tua terra nativa, del perché te ne sei andato, o di cosa ti sei lasciato alle spalle. Se gli rispondi, segna un PE.",
      grantedMoveId: 'barbaro.archetipo-straniero',
    },
    {
      id: 'selvaggio',
      name: 'Selvaggio',
      // associatedRace omesso (undefined)
      description:
        'Quando la tua letalità viene sottostimata per via del tuo aspetto primitivo, ottieni +1 al prossimo tiro di *taglia e spacca*.',
      grantedMoveId: 'barbaro.archetipo-selvaggio',
    },
    {
      id: 'nomade',
      name: 'Nomade',
      // associatedRace omesso (undefined)
      description:
        'Quando ti *accampi* nelle terre selvagge, non hai bisogno di consumare una *razione*.',
      grantedMoveId: 'barbaro.archetipo-nomade',
    },
  ],

  // Manuale V1, rr. 1995-2020. 3 mosse iniziali automatiche.
  startingMoves: barbaroStartingAutoMoves.map((m) => m.id),

  // Manuale V1, rr. 2023-2052. **Due gruppi di scelta iniziale** — pattern non
  // visto nelle 11 classi precedenti. Il **secondo gruppo (Istinti) usa
  // `expandableInAdvanced: true`** — primo uso strutturale del flag.
  startingChoiceGroups: [
    {
      id: 'stili-di-difesa',
      name: 'Stile di Difesa',
      description:
        "Scegli il tuo stile di difesa: *Coperto d'Acciaio* (ignori *ingombrante* sull'armatura) o *Mai Impacciato, Sempre Illeso* (+1 Armatura senza armatura/scudo se peso < Carico max).",
      moveOptions: barbaroDefenseStyleMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
    {
      id: 'istinti',
      name: 'Istinto',
      description:
        'Scegli un Istinto: *Avventura: Un Amante in Ogni Porto* (CAR, vecchie fiamme), *Battaglia: Un Fascio di Muscoli* (etichette *brutale*+*impatto*), o *Misticismo: Animale Totemico* (mossa-config con 4 specie: aquila/corvo/lupo/orso). Gli Istinti non scelti al L1 possono essere presi al level-up tramite *Istinti Primordiali* (L2-5).',
      moveOptions: barbaroInstinctMoves.map((m) => m.id),
      pickCount: 1,
      // PRIMO USO STRUTTURALE del flag fra le 12 classi DWR. Tracciato in FI-31
      // come parte del pattern trasversale «mossa avanzata che concede
      // sotto-scelta da un set della classe».
      expandableInAdvanced: true,
    },
  ],

  advancedMoves2to5: barbaroAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: barbaroAdvanced6to10Moves.map((m) => m.id),

  // Il Barbaro NON è una classe casteggiante: niente *Lanciare Incantesimi*,
  // *Mana*, Tradizioni Sacre o spell list. 7° classe non casteggiante DWR
  // (Barbaro/Bardo/Guerriero/Ladro/Monaco/Ranger/Sciamano).
  // spellcasting resta undefined.

  // Manuale V1, rr. 1973-1983
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'pugnale',
        name: 'Pugnale',
        weight: 1,
        tags: ['corta'],
      },
      {
        id: 'ricordo-della-terra',
        name: 'Ricordo della tua terra',
        weight: 0,
        tags: [],
        userDescriptionPrompt:
          'Un ricordo della tua terra o di un posto che hai visitato. Descrivilo a tua scelta.',
      },
    ],
    choices: [
      {
        id: 'arma-iniziale',
        prompt: 'Scegli la tua arma',
        pickCount: 1,
        options: [
          {
            id: 'ascia',
            label: 'Ascia',
            items: [
              {
                id: 'ascia',
                name: 'Ascia',
                weight: 1,
                tags: ['media'],
              },
            ],
          },
          {
            id: 'spada-lunga',
            label: 'Spada lunga (+1 danni)',
            items: [
              {
                id: 'spada-lunga',
                name: 'Spada lunga',
                weight: 2,
                tags: ['media'],
                damage: '+1 danni',
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'avventuriero-razioni',
            label: 'Attrezzatura da Avventuriero + Razioni da Dungeon',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
              },
              {
                id: 'razioni-da-dungeon-extra',
                name: 'Razioni da Dungeon',
                weight: 1,
                uses: 5,
                tags: ['razione'],
              },
            ],
          },
          {
            id: 'maglia-di-ferro',
            label: 'Maglia di ferro (1 Armatura)',
            items: [
              {
                id: 'maglia-di-ferro',
                name: 'Maglia di ferro',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
            ],
          },
        ],
      },
    ],
  },
};
